import config from '../config';
import { getPriceRange } from './pricing';
import { getAggregateRating, buildReviewSchemaList } from './reviews';

// Reusable Schema.org (JSON-LD) builders. Structured-data values come from
// config (the single source of truth) so markup never drifts from what the site
// actually shows. See Home.js for the VacationRental schema that lives alongside
// these.

// Builds a LodgingBusiness node describing the property as a local business.
// This is the entity Google associates with Maps/local results, so it
// complements the VacationRental schema and reinforces the Google Business
// Profile (set config.googleBusinessUrl once the profile is live to link them).
export const buildLodgingBusinessSchema = (baseUrl) => {
    const { property, contact, propertyName } = config;

    // External profiles that describe the same business. Anything not configured
    // (social handles, the GBP URL before it exists) is filtered out.
    const sameAs = [
        config.providers?.airbnb?.url,
        config.providers?.booking?.url,
        config.googleBusinessUrl,
        config.social?.instagram,
        config.social?.facebook,
    ].filter(Boolean);

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'LodgingBusiness',
        '@id': `${baseUrl}/#lodging`,
        name: propertyName,
        description: property.description,
        url: baseUrl,
        image: property.images.map((img) => `${baseUrl}${img}`),
        telephone: contact.phone,
        email: contact.email,
        address: {
            '@type': 'PostalAddress',
            streetAddress: property.streetAddress,
            addressLocality: property.addressLocality,
            addressRegion: property.addressRegion,
            postalCode: property.postalCode,
            addressCountry: property.addressCountry,
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: property.geo.latitude,
            longitude: property.geo.longitude,
        },
        amenityFeature: property.amenities.map((amenity) => ({
            '@type': 'LocationFeatureSpecification',
            name: amenity,
        })),
        numberOfRooms: property.numberOfBedrooms,
    };

    const priceRange = getPriceRange();
    if (priceRange) {
        schema.priceRange = priceRange;
    }

    if (sameAs.length) {
        schema.sameAs = sameAs;
    }

    // The aggregate rating + individual reviews live on this single
    // authoritative business entity (computed from reviews.json, never
    // hardcoded). Omitted entirely when there are no reviews.
    const aggregateRating = getAggregateRating();
    if (aggregateRating) {
        schema.aggregateRating = aggregateRating;
        schema.review = buildReviewSchemaList();
    }

    return schema;
};

// Builds a BreadcrumbList from an ordered list of { name, path } crumbs. Each
// `path` is appended to baseUrl to form the item URL (the last crumb may omit
// `path` for the current page). Improves how nested pages display in search.
export const buildBreadcrumbSchema = (items, baseUrl) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(item.path ? { item: `${baseUrl}${item.path}` } : {}),
    })),
});
